import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import "../../CSS/Barcodelabel.css";
import api from "../../../src/api.js";
import { useRights } from "../../Components/RightsContext.jsx";

/* ══════════════════════════════════════════════════
   COMPANY / DATA
══════════════════════════════════════════════════ */
const Barcodlabel_PATH = "/Reports/Barcodelabe";
const COMPANY = "SAAMI TRADESTAR";

const SIZES = {
  "100x150": { w: 100, h: 150 },
  "100x100": { w: 100, h: 100 },
  "100x75": { w: 100, h: 75 },
  "100x50": { w: 100, h: 50 },
  "75x50": { w: 75, h: 50 },
};

/* ══════════════════════════════════════════════════
   CODE 128 (subset B) — returns bar rects for a barcode SVG
══════════════════════════════════════════════════ */
const C128 = ["212222","222122","222221","121223","121322","131222","122213","122312","132212","221213","221312","231212","112232","122132","122231","113222","123122","123221","223211","221132","221231","213212","223112","312131","311222","321122","321221","312212","322112","322211","212123","212321","232121","111323","131123","131321","112313","132113","132311","211313","231113","231311","112133","112331","132131","113123","113321","133121","313121","211331","231131","213113","213311","213131","311123","311321","331121","312113","312311","332111","314111","221411","431111","111224","111422","121124","121421","141122","141221","112214","112412","122114","122411","142112","142211","241211","221114","413111","241112","134111","111242","121142","121241","114212","124112","124211","411212","421112","421211","212141","214121","412121","111143","111341","131141","114113","114311","411113","411311","113141","114131","311141","411131","211412","211214","211232","2331112"];

function code128Rects(text) {
  const vals = [];
  for (const ch of text) {
    let v = ch.charCodeAt(0) - 32;
    if (v < 0 || v > 94) v = 0;
    vals.push(v);
  }
  let sum = 104;
  vals.forEach((v, i) => (sum += v * (i + 1)));
  const seq = [104, ...vals, sum % 103, 106];
  let x = 0;
  const rects = [];
  seq.forEach((code) => {
    const pat = C128[code];
    for (let k = 0; k < pat.length; k++) {
      const w = +pat[k];
      if (k % 2 === 0) rects.push({ x, width: w });
      x += w;
    }
  });
  return { rects, totalWidth: x };
}

function Barcode({ text }) {
  const { rects, totalWidth } = code128Rects(text);
  return (
    <svg viewBox={`0 0 ${totalWidth} 100`} preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
      <g fill="#111">
        {rects.map((r, i) => (
          <rect key={i} x={r.x} y="0" width={r.width} height="100" />
        ))}
      </g>
    </svg>
  );
}

/* ══════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════ */
function fmtDate(d) {
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

/* ══════════════════════════════════════════════════
   LABEL — shared markup for a single piece label
══════════════════════════════════════════════════ */
function Label({ c, piece, total, showText, style }) {
  const code = `${c.cnr}-${String(piece).padStart(2, "0")}`;
  return (
    <div className="bcl-lbl" style={style}>
      <div className="bcl-lbl-top">
        <div className="bcl-lbl-co">{COMPANY}</div>
        <div className="bcl-lbl-svc">{c.service} · {c.mode}</div>
      </div>
      <div className="bcl-lbl-route">
        <span className="bcl-lbl-orig">{c.origin}</span>
        <span className="bcl-lbl-arrow">→</span>
        <span className="bcl-lbl-dest">{c.dest}</span>
      </div>
      <div className="bcl-lbl-place">{c.originName} &nbsp;→&nbsp; {c.destName}</div>
      <div className="bcl-lbl-cnr">{code}</div>
      <div className="bcl-lbl-barcode"><Barcode text={code} /></div>
      {showText ? <div className="bcl-lbl-bc-text">{code}</div> : <div style={{ height: ".2em" }} />}
      <div className="bcl-lbl-mid">
        <div className="bcl-lbl-piece"><span>PIECE</span><b>{piece}/{total}</b></div>
        <div className="bcl-lbl-info">
          <div><b>Wt:</b> {c.weight.toFixed(1)} kg</div>
          <div><b>Date:</b> {fmtDate(c.date)}</div>
          <div><b>Pin:</b> {c.pin}</div>
        </div>
      </div>
      <div className="bcl-lbl-to">
        <div className="bcl-lbl-to-l">DELIVER TO</div>
        <div className="bcl-lbl-to-name">{c.consignee}</div>
        <div className="bcl-lbl-to-addr">{c.addr}</div>
        <div className="bcl-lbl-to-addr">Pin: {c.deliverPin}</div>
        <div className="bcl-lbl-to-ph">📞 {c.mobile}</div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   TOASTS
══════════════════════════════════════════════════ */
function ToastContainer({ toasts, onDismiss }) {
  const icons = { ok: "✓", err: "✕", warn: "⚠", info: "ℹ" };
  return (
    <div className="bcl-toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`bcl-toast bcl-${t.type}${t.leaving ? " bcl-leaving" : ""}`}>
          <span style={{ fontSize: 15 }}>{icons[t.type] || "ℹ"}</span>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
export default function Barcodelabel({ autoCnr, onClose, compact, onNewBooking }) {
    const rights = useRights(Barcodlabel_PATH);
  const [cnrInput, setCnrInput] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [queue, setQueue] = useState([]);
  const [optSize, setOptSize] = useState("100x100");
  const [optCopies, setOptCopies] = useState(1);
  const [optText, setOptText] = useState("1");
  const [toasts, setToasts] = useState([]);
  const inputRef = useRef(null);
  const autoAddedRef = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-add the just-booked consignment (from ConsignmentBooking) so the
  // page opens straight into a ready-to-print pcs-wise label queue —
  // no manual scan/entry needed. Guarded to run only once per autoCnr value.
  useEffect(() => {
    if (autoCnr && !autoAddedRef.current) {
      autoAddedRef.current = true;
      addCnr(String(autoCnr));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCnr]);

  /* ── TOAST ── */
  const toast = useCallback((msg, type = "info") => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg, type, leaving: false }]);
    setTimeout(() => {
      setToasts((t) => t.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
      setTimeout(() => {
        setToasts((t) => t.filter((x) => x.id !== id));
      }, 320);
    }, 2700);
  }, []);

  function dismissToast(id) {
    setToasts((t) => t.filter((x) => x.id !== id));
  }

  /* ── ADD / QUEUE ── */
  async function addCnr(valueOverride) {
    const raw = (valueOverride ?? cnrInput).trim();
    if (!raw) {
      toast("Enter / scan a consignment number", "warn");
      return;
    }

    const consignmentNo = parseInt(raw.replace(/\D/g, ""), 10);
    if (!consignmentNo || consignmentNo <= 0) {
      toast("Enter a valid consignment number", "warn");
      return;
    }

    if (queue.some((q) => String(q.cnr) === String(consignmentNo))) {
      toast(consignmentNo + " is already in the queue", "info");
      setCnrInput("");
      return;
    }

    setIsAdding(true);
    try {
      const res = await api.get("/get-cons-label-data", { params: { consignmentNo } });

      if (!res.data?.success || !res.data?.data) {
        toast(res.data?.message || "Consignment " + consignmentNo + " not found", "err");
        return;
      }

      const d = res.data.data;
      const pcs = Number(d.no_of_pkg ?? d.No_of_pkg) || 1;

      const c = {
        cnr: d.consignment_No ?? d.Consignment_No ?? consignmentNo,
        consignor: d.consignor_Name ?? d.Consignor_Name ?? d.party_Name ?? d.Party_Name ?? "",
        consignee: d.consignee_Name ?? d.Consignee_Name ?? "",
        origin: d.origin ?? d.Origin ?? "",
        originName: d.origin_Place ?? d.Origin_Place ?? "",
        dest: d.dest ?? d.Dest ?? "",
        destName: d.destination_Place ?? d.Destination_Place ?? "",
        addr: d.consignee_Address ?? d.Consignee_Address ?? d.address ?? d.Address ?? "",
        deliverPin: d.consignee_Pincode ?? d.Consignee_Pincode ?? "",
        pin: d.pincode ?? d.Pincode ?? "",
        mobile: d.consignee_Phone ?? d.Consignee_Phone ?? d.phone_No ?? d.Phone_No ?? "",
        pcs,
        weight: Number(d.chrg_weight ?? d.Chrg_weight ?? d.chrg_Weight) || 0,
        date: d.book_Dt ?? d.Book_Dt ?? new Date().toISOString(),
        service: "Surface",
        mode: "LTL",
      };

      setQueue((q) => [...q, c]);
      setCnrInput("");
      inputRef.current?.focus();
      toast(c.cnr + " added — " + c.pcs + " piece labels", "ok");
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to fetch consignment " + consignmentNo;
      toast(msg, "err");
    } finally {
      setIsAdding(false);
    }
  }

  function removeFromQueue(cnr) {
    setQueue((q) => q.filter((item) => item.cnr !== cnr));
  }

  function clearQueue() {
    setQueue([]);
  }

  /* ── DERIVED: all labels (queue × pieces × copies) ── */
  const copies = Math.max(1, Math.min(9, parseInt(optCopies) || 1));
  const showText = optText === "1";
  const size = SIZES[optSize];

  const allLabels = [];
  queue.forEach((c) => {
    for (let p = 1; p <= c.pcs; p++) {
      for (let k = 0; k < copies; k++) allLabels.push({ c, piece: p, total: c.pcs });
    }
  });
  const totalLabels = allLabels.length;

  const portrait = size.h >= size.w;
  const prevH = portrait ? 360 : 210;
  const prevW = prevH * (size.w / size.h);
  const fs = Math.min(prevW / 22, prevH / 34);

  /* ── PRINT ── */
  function printLabels() {
    if (!totalLabels) {
      toast("Nothing to print", "warn");
      return;
    }
    toast("Sending " + totalLabels + " labels to printer…", "info");
    setTimeout(() => window.print(), 250);
  }

  const printFs = Math.min(size.w / 22, size.h / 34);

  return (
    <div className="bcl-root">


      {compact && onClose && (
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 20px 0" }}>
          <button className="bcl-btn bcl-btn-ghost bcl-btn-sm" onClick={onClose}>✕ Close</button>
        </div>
      )}

      <div className="bcl-page">
        {!compact && (
          <div className="bcl-pg-row">
            <div className="bcl-pg-title">
              <h1>Consignment Barcode Labels</h1>
              <p>Scan or enter a consignment — one Code-128 label per piece, numbered 1/N … N/N. Build a print queue, then print.</p>
            </div>
          </div>
        )}

        <div className="bcl-layout">
          {/* LEFT */}
          <div>
            {!compact && (
            <div className="bcl-card">
              <div className="bcl-band bcl-teal-band">
                <div className="bcl-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M3 5v14M7 5v14M11 5v14M15 5v14M19 5v14" /></svg>
                </div>
                <div>
                  <div className="bcl-band-title">Add Consignment</div>
                  <div className="bcl-band-sub">Scan / enter consignment no</div>
                </div>
              </div>
              <div className="bcl-card-body">
                <div className="bcl-scan-box">
                  <span className="bcl-scan-ico">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 5v14M7 5v14M11 5v14M15 5v14M19 5v14" /></svg>
                  </span>
                  <input
                    ref={inputRef}
                    className="bcl-cnr-input"
                    placeholder="Enter / scan consignment no"
                    autoComplete="off"
                    spellCheck="false"
                    value={cnrInput}
                    disabled={isAdding}
                    onChange={(e) => setCnrInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addCnr(); }}
                  />
                  {rights.add && (<button className="bcl-scan-go" onClick={() => addCnr()} disabled={isAdding}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg> {isAdding ? "Checking…" : "Add"}
                  </button>)}
                </div>
              </div>
            </div>
            )}

            {!compact && (
            <div className="bcl-card">
              <div className="bcl-band bcl-navy-band">
                <div className="bcl-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /></svg>
                </div>
                <div><div className="bcl-band-title">Print Queue</div></div>
                <div className="bcl-band-r"><span className="bcl-band-badge">{queue.length}</span></div>
              </div>
              <div className="bcl-card-body">
                <div>
                  {queue.length === 0 ? (
                    <div className="bcl-q-empty"><div className="bcl-qe-ico">📭</div>Queue is empty</div>
                  ) : (
                    queue.map((c) => (
                      <div key={c.cnr} className="bcl-q-item">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="bcl-q-cnr">{c.cnr}</div>
                          <div className="bcl-q-meta">{c.consignee} · {c.originName} → {c.destName}</div>
                        </div>
                        <span className="bcl-q-pcs">{c.pcs} pcs</span>
                        <button className="bcl-q-rm" onClick={() => removeFromQueue(c.cnr)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <button className="bcl-btn bcl-btn-ghost bcl-btn-block bcl-btn-sm" style={{ marginTop: 10 }} onClick={clearQueue}>Clear Queue</button>
              </div>
            </div>
            )}

            <div className="bcl-card">
              <div className="bcl-band bcl-navy-band">
                <div className="bcl-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>
                </div>
                <div><div className="bcl-band-title">Label Options</div></div>
              </div>
              <div className="bcl-card-body">
                <div className="bcl-field">
                  <label>Label Size</label>
                  <select value={optSize} onChange={(e) => setOptSize(e.target.value)}>
                    <option value="100x150">4″ × 6″  (100 × 150 mm)</option>
                    <option value="100x100">4″ × 4″  (100 × 100 mm)</option>
                    <option value="100x75">4″ × 3″  (100 × 75 mm)</option>
                    <option value="100x50">4″ × 2″  (100 × 50 mm)</option>
                    <option value="75x50">3″ × 2″  (75 × 50 mm)</option>
                  </select>
                </div>
                <div className="bcl-opt-2">
                  <div className="bcl-field">
                    <label>Copies / piece</label>
                    <input type="number" min="1" max="9" value={optCopies} onChange={(e) => setOptCopies(e.target.value)} />
                  </div>
                  <div className="bcl-field">
                    <label>Show Barcode Text</label>
                    <select value={optText} onChange={(e) => setOptText(e.target.value)}>
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT : PREVIEW */}
          <div className="bcl-card" style={{ margin: 0 }}>
            <div className="bcl-band bcl-teal-band">
              <div className="bcl-band-icon">
                <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              </div>
              <div>
                <div className="bcl-band-title">Label Preview</div>
                <div className="bcl-band-sub">
                  {queue.length ? `${queue.length} consignment${queue.length > 1 ? "s" : ""} · ${totalLabels} labels` : "Add a consignment to generate labels"}
                </div>
              </div>
              <div className="bcl-band-r"><span className="bcl-band-badge">{totalLabels} label{totalLabels !== 1 ? "s" : ""}</span></div>
            </div>
            <div>
              {totalLabels === 0 ? (
                <div className="bcl-prev-empty">
                  <div className="bcl-pe-ico">🏷️</div>
                  <div className="bcl-pe-t">No labels yet</div>
                  <div className="bcl-pe-s">Scan or enter a consignment number to build piece labels</div>
                </div>
              ) : (
                queue.map((c) => (
                  <div key={c.cnr}>
                    <div className="bcl-prev-head">
                      <span className="bcl-ph-cnr">{c.cnr}</span>
                      <span className="bcl-ph-route">{c.consignee} · {c.originName}→{c.destName}</span>
                      <span className="bcl-ph-r">{c.pcs} pcs × {copies} = {c.pcs * copies} labels</span>
                    </div>
                    <div className="bcl-labels-grid">
                      {Array.from({ length: c.pcs }, (_, i) => i + 1).flatMap((p) =>
                        Array.from({ length: copies }, (_, k) => (
                          <Label
                            key={`${c.cnr}-${p}-${k}`}
                            c={c}
                            piece={p}
                            total={c.pcs}
                            showText={showText}
                            style={{ width: prevW, height: prevH, fontSize: fs }}
                          />
                        ))
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PRINT BAR */}
      {/* <div className="bcl-print-bar">
        <div className="bcl-pb-inner">
          <div className="bcl-pb-info"><b>{totalLabels}</b> labels ready to print</div>
          <div className="bcl-pb-actions">
            {onNewBooking ? (
              <button className="bcl-btn bcl-btn-ghost" onClick={onNewBooking}>+ New Booking</button>
            ) : (
              <button className="bcl-btn bcl-btn-ghost" onClick={clearQueue}>Reset</button>
            )}
            <button className="bcl-btn bcl-btn-navy bcl-btn-lg" onClick={printLabels} disabled={totalLabels === 0}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg> Print Labels
            </button>
          </div>
        </div>
      </div>
//
      <ToastContainer toasts={toasts} onDismiss={dismissToast} /> */}

      {/* PRINT AREA — rendered via portal directly into document.body so it's
          a true top-level sibling of the whole app, not nested inside any
          app-shell layout. This lets @media print hide everything else with
          plain display:none (no leftover blank pages from hidden layout space). */}
          {createPortal(
        <div id="bcl-print-portal">
          <style>{`@media print{@page{size:${size.w}mm ${size.h}mm;margin:0;}
            .bcl-print-label{width:${size.w}mm;height:${size.h}mm;}
            .bcl-print-label .bcl-lbl{font-size:${printFs}mm;}}`}</style>
          {allLabels.map((l, i) => (
            <div key={i} className="bcl-print-label">
              <Label c={l.c} piece={l.piece} total={l.total} showText={showText} />
            </div>
          ))}
        </div>,
        document.body
      )}
      
      {createPortal(
        <div className="bcl-fixed-portal">
          <div className="bcl-print-bar">
            <div className="bcl-pb-inner">
              <div className="bcl-pb-info"><b>{totalLabels}</b> labels ready to print</div>
              <div className="bcl-pb-actions">
                {onNewBooking ? (
                  <button className="bcl-btn bcl-btn-ghost" onClick={onNewBooking}>+ New Booking</button>
                ) : (
                  <button className="bcl-btn bcl-btn-ghost" onClick={clearQueue}>Reset</button>
                )}
               {rights.export && ( <button className="bcl-btn bcl-btn-navy bcl-btn-lg" onClick={printLabels} disabled={totalLabels === 0}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg> Print Labels
                </button>)}
              </div>
            </div>
          </div>
          <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        </div>,
        document.body
      )}
    </div>
  );
}
